/* eslint-disable react/prop-types */
import React, { useState, useMemo, useEffect } from 'react';
import TrebbleCard from '../common/TrebbleCard';
import TrebbleButton from '../common/TrebbleButton';
import ModalHeader from '../common/ModalHeader';
import SelectableOptionCard from './SelectableOptionCard';
import TrebbleSegmented from '../common/TrebbleSegmented';
import StripeConfig from '../../models/helper/StripeConfig';
import WorkspacePricingCatalog from '../../models/helper/WorkspacePricingCatalog';

// Fallback display prices used when /api/v1/billing/plans is unreachable.
// Prefer the authoritative Stripe-backed values returned from
// StripeConfig.fetchPricingCatalog(); these hardcoded values exist only to
// keep the UI responsive while the fetch is in flight and as a last-resort
// default if the backend is unavailable. Sourced from
// models/helper/WorkspacePricingCatalog.js so every billing surface stays
// in sync with the published price list.
const FALLBACK_PLAN_PRICE_DISPLAY = WorkspacePricingCatalog.FALLBACK_PLAN_PRICES;

// Mirrors trebbleNodeServer/helpers/WorkspaceRulesHelper.js PLAN_LIMITS.
// Infinity is represented as null on this side so that JSON-like comparisons
// stay simple. The UI renders null/Infinity as "Unlimited".
//
// Feature numbers (hours/storage/editors/reviewers) come from
// WorkspacePricingCatalog.FALLBACK_PLAN_FEATURES so pricing drift cannot
// produce a plan card whose limits contradict its price.
const _FEATURES = WorkspacePricingCatalog.FALLBACK_PLAN_FEATURES;
const FALLBACK_PLAN_CATALOG = [
    {
        planType: 'free',
        displayNameKey: 'FREE_PLAN_NAME',
        // Free is owner-only: maxEditors=1 (the owner IS an editor seat). 0 would
        // make the cancel-to-free flow wrongly warn a paid workspace with just
        // the owner that it already exceeds Free's cap.
        limits: {
            maxEditors: _FEATURES.free.editors,
            maxReviewersPerMonth: _FEATURES.free.reviewersPerMonth,
            maxHoursPerMonth: _FEATURES.free.hours,
            maxStorageGB: _FEATURES.free.storageGB,
        },
    },
    {
        planType: 'starter',
        displayNameKey: 'STARTER_PLAN_NAME',
        // Starter: owner only (1 editor seat). Backend PLAN_LIMITS.starter.maxEditors=1
        // reflects this — matching here so the Pro→Starter downgrade dialog warns
        // about over-capacity from first render, not only after /billing/plans loads.
        limits: {
            maxEditors: _FEATURES.starter.editors,
            maxReviewersPerMonth: _FEATURES.starter.reviewersPerMonth,
            maxHoursPerMonth: _FEATURES.starter.hours,
            maxStorageGB: _FEATURES.starter.storageGB,
        },
    },
    {
        planType: 'pro',
        displayNameKey: 'PRO_PLAN_NAME',
        // Pro: owner + 1 editor = 2 total seats (matches PLAN_LIMITS.pro.maxEditors)
        limits: {
            maxEditors: _FEATURES.pro.editors,
            maxReviewersPerMonth: _FEATURES.pro.reviewersPerMonth,
            maxHoursPerMonth: _FEATURES.pro.hours,
            maxStorageGB: _FEATURES.pro.storageGB,
        },
    },
    {
        planType: 'team',
        displayNameKey: 'TEAM_PLAN_NAME',
        // Team: 5 included seats, extras via Stripe extra-seat line item.
        // maxHoursPerMonth=null means "Unlimited" — the catalog constant is 100
        // hrs included but Team does not enforce a monthly hours cap.
        limits: {
            maxEditors: _FEATURES.team.editors,
            maxReviewersPerMonth: _FEATURES.team.reviewersPerMonth,
            maxHoursPerMonth: null,
            maxStorageGB: _FEATURES.team.storageGB,
        },
    },
];

// Re-exported for legacy importers that referenced PLAN_CATALOG directly.
const PLAN_CATALOG = FALLBACK_PLAN_CATALOG;

function formatCurrencyAmount(amountInDollars, currency) {
    const normalized = (currency || 'usd').toLowerCase();
    const rounded = Math.round(Number(amountInDollars) * 100) / 100;
    const prefix = normalized === 'cad' ? 'CA$' : '$';
    return prefix + rounded.toFixed(2);
}

function normalizeBackendPlan(backendPlan, displayNameKey) {
    // includedReviewers: null on the wire means unlimited (matches frontend convention).
    const currency = backendPlan.currency || 'usd';
    const monthlyCents = Number(backendPlan.amountMonthly);
    const yearlyCents = Number(backendPlan.amountYearly);
    return {
        planType: backendPlan.planType,
        displayNameKey: displayNameKey,
        limits: {
            maxEditors: backendPlan.includedEditors === null ? null : Number(backendPlan.includedEditors),
            maxReviewersPerMonth: backendPlan.includedReviewers === null ? null : Number(backendPlan.includedReviewers),
            maxHoursPerMonth: backendPlan.includedHours === null ? null : Number(backendPlan.includedHours),
            maxStorageGB: backendPlan.includedStorageGB === null ? null : Number(backendPlan.includedStorageGB),
        },
        price: {
            monthly: isFinite(monthlyCents) ? monthlyCents / 100 : 0,
            yearly: isFinite(yearlyCents) ? yearlyCents / 100 : 0,
            currency: currency,
        },
    };
}

const DISPLAY_NAME_KEY_BY_PLAN = {
    free: 'FREE_PLAN_NAME',
    starter: 'STARTER_PLAN_NAME',
    pro: 'PRO_PLAN_NAME',
    team: 'TEAM_PLAN_NAME',
};

function bytesToGB(bytes) {
    if (bytes == null || isNaN(bytes)) return 0;
    return Math.round((Number(bytes) / (1024 * 1024 * 1024)) * 10) / 10;
}

function isLimitUnlimited(value) {
    return value === null || value === undefined || value === Infinity;
}

function formatLimit(value, t, suffix) {
    if (isLimitUnlimited(value)) return t('UNLIMITED');
    return suffix ? value + ' ' + suffix : String(value);
}

function templateString(template, vars) {
    return String(template).replace(/\$\{(\w+)\}/g, function (_match, key) {
        return vars[key] != null ? vars[key] : '';
    });
}

function toFiniteNumber(value, fallback) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
}

function normalizeBillingCycle(value) {
    const normalized = (value || 'monthly').toString().trim().toLowerCase();
    return normalized === 'annual' || normalized === 'yearly' || normalized === 'year'
        ? 'yearly'
        : 'monthly';
}

const ChangePlanContent = ({
    currentPlanType,
    currentBillingCycle,
    workspaceUsage,
    onConfirm,
    onCancel,
    onCancelSubscription,
    i18nModule,
    useEditorTheme = true,
}) => {
    const t = (key) => window.getI18n(i18nModule, key);

    const normalizedCurrentPlan = (currentPlanType || 'free').toLowerCase();
    const normalizedCurrentBillingCycle = normalizeBillingCycle(currentBillingCycle);
    const usage = workspaceUsage || {};
    const currentEditors = toFiniteNumber(usage.editorsCount, 0);
    const activeEditorSeats = toFiniteNumber(usage.activeEditorSeats, currentEditors);
    const pendingEditorInvitesCount = Math.max(0, toFiniteNumber(usage.pendingEditorInvitesCount, Math.max(0, currentEditors - activeEditorSeats)));
    const currentReviewers = toFiniteNumber(usage.reviewersCount, 0);
    const currentHoursUsed = toFiniteNumber(usage.hoursUsed, 0);
    const currentStorageGB = bytesToGB(usage.storageUsedBytes);

    const [selectedPlanType, setSelectedPlanType] = useState(normalizedCurrentPlan);
    const [selectedBillingCycle, setSelectedBillingCycle] = useState(normalizedCurrentBillingCycle);
    const [submitting, setSubmitting] = useState(false);

    // Pricing catalog state. Starts with hardcoded fallbacks so the UI paints
    // immediately; swapped for backend values once fetchPricingCatalog resolves.
    const [planCatalog, setPlanCatalog] = useState(FALLBACK_PLAN_CATALOG);
    const [planPriceDisplay, setPlanPriceDisplay] = useState(FALLBACK_PLAN_PRICE_DISPLAY);

    useEffect(() => {
        let cancelled = false;
        if (!StripeConfig || typeof StripeConfig.fetchPricingCatalog !== 'function') {
            return () => {};
        }
        StripeConfig.fetchPricingCatalog().then((catalog) => {
            if (cancelled || !catalog || !Array.isArray(catalog.plans)) return;
            const nextCatalog = [];
            const nextPriceDisplay = {};
            catalog.plans.forEach((backendPlan) => {
                if (!backendPlan || !backendPlan.planType) return;
                const displayNameKey = DISPLAY_NAME_KEY_BY_PLAN[backendPlan.planType];
                if (!displayNameKey) return;
                const normalized = normalizeBackendPlan(backendPlan, displayNameKey);
                nextCatalog.push(normalized);
                nextPriceDisplay[normalized.planType] = {
                    monthly: normalized.price.monthly,
                    yearly: normalized.price.yearly,
                    currency: normalized.price.currency,
                };
            });
            if (nextCatalog.length > 0) {
                // Preserve plan ordering (free -> starter -> pro -> team)
                const orderIndex = { free: 0, starter: 1, pro: 2, team: 3 };
                nextCatalog.sort((a, b) => (orderIndex[a.planType] || 0) - (orderIndex[b.planType] || 0));
                setPlanCatalog(nextCatalog);
                setPlanPriceDisplay({ ...FALLBACK_PLAN_PRICE_DISPLAY, ...nextPriceDisplay });
            }
        }).catch((err) => {
            console.warn('ChangePlanContent: pricing catalog fetch failed, using fallback', err);
        });
        return () => { cancelled = true; };
    }, []);

    const planRanking = { free: 0, starter: 1, pro: 2, team: 3 };
    const currentRank = planRanking[normalizedCurrentPlan] != null ? planRanking[normalizedCurrentPlan] : 0;

    const exceedances = useMemo(() => {
        const target = planCatalog.find((p) => p.planType === selectedPlanType);
        if (!target) return [];
        const issues = [];
        const limits = target.limits;
        if (!isLimitUnlimited(limits.maxEditors) && currentEditors > limits.maxEditors) {
            const messageKey = pendingEditorInvitesCount > 0
                ? 'DOWNGRADE_RESERVED_EDITORS_EXCEED'
                : 'DOWNGRADE_EDITORS_EXCEED';
            issues.push({
                blocking: true,
                message: templateString(t(messageKey), {
                    current: currentEditors,
                    active: activeEditorSeats,
                    pending: pendingEditorInvitesCount,
                    limit: limits.maxEditors,
                }),
            });
        }
        if (!isLimitUnlimited(limits.maxReviewersPerMonth) && currentReviewers > limits.maxReviewersPerMonth) {
            issues.push({
                blocking: true,
                message: templateString(t('DOWNGRADE_REVIEWERS_EXCEED'), { current: currentReviewers, limit: limits.maxReviewersPerMonth }),
            });
        }
        if (!isLimitUnlimited(limits.maxStorageGB) && currentStorageGB > limits.maxStorageGB) {
            issues.push({
                blocking: true,
                message: templateString(t('DOWNGRADE_STORAGE_EXCEED'), { current: currentStorageGB, limit: limits.maxStorageGB }),
            });
        }
        if (!isLimitUnlimited(limits.maxHoursPerMonth) && currentHoursUsed > limits.maxHoursPerMonth) {
            issues.push({
                blocking: false,
                message: templateString(t('DOWNGRADE_HOURS_EXCEED'), { current: currentHoursUsed, limit: limits.maxHoursPerMonth }),
            });
        }
        return issues;
    }, [selectedPlanType, currentEditors, activeEditorSeats, pendingEditorInvitesCount, currentReviewers, currentStorageGB, currentHoursUsed, planCatalog]);

    const handleConfirm = async () => {
        if (submitting) return;
        if (selectedPlanType === normalizedCurrentPlan && selectedBillingCycle === normalizedCurrentBillingCycle) return;
        setSubmitting(true);
        try {
            await onConfirm({
                planType: selectedPlanType,
                billingCycle: selectedBillingCycle,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (submitting) return;
        if (typeof onCancelSubscription !== 'function') {
            // No handler wired — surface the legacy hint rather than swallowing.
            console.warn('ChangePlanContent: onCancelSubscription handler is not configured');
            return;
        }
        setSubmitting(true);
        try {
            await onCancelSubscription();
        } finally {
            setSubmitting(false);
        }
    };

    const isSelectionUnchanged = selectedPlanType === normalizedCurrentPlan && selectedBillingCycle === normalizedCurrentBillingCycle;
    const targetRank = planRanking[selectedPlanType] != null ? planRanking[selectedPlanType] : 0;
    const isDowngrade = targetRank < currentRank;
    // Free downgrades from a paid plan must use the cancelSubscription flow.
    const isFreeFromPaid = selectedPlanType === 'free' && normalizedCurrentPlan !== 'free';
    const hasBlockingDowngradeExceedance = !isFreeFromPaid && isDowngrade && exceedances.some((issue) => issue.blocking);

    return (
        <TrebbleCard useEditorTheme={useEditorTheme} className="p-6">
            <ModalHeader
                title={t('TITLE')}
                subtitle={t('SUBTITLE')}
                useEditorTheme={useEditorTheme}
            />

            <TrebbleSegmented
                aria-label={t('BILLING_CYCLE_LABEL')}
                value={selectedBillingCycle}
                onChange={(value) => setSelectedBillingCycle(normalizeBillingCycle(value))}
                options={[
                    { label: t('BILLING_CYCLE_MONTHLY'), value: 'monthly' },
                    { label: t('BILLING_CYCLE_YEARLY'), value: 'yearly' },
                ]}
                useEditorTheme={useEditorTheme}
                className="mb-4"
            />

            <div
                className="grid gap-3 mb-5"
                style={{
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                }}
            >
                {planCatalog.map((plan) => {
                    const isCurrent = plan.planType === normalizedCurrentPlan && selectedBillingCycle === normalizedCurrentBillingCycle;
                    const isSelected = plan.planType === selectedPlanType;
                    const price = planPriceDisplay[plan.planType] || FALLBACK_PLAN_PRICE_DISPLAY[plan.planType] || { monthly: 0, yearly: 0, currency: 'usd' };
                    const priceAmount = selectedBillingCycle === 'yearly' && price.yearly != null ? price.yearly : price.monthly;
                    // The "free" card should stay selectable from any paid plan so the user
                    // can use the new in-popup Cancel Subscription CTA. Previously it was
                    // disabled with only a hint directing users elsewhere.
                    const disablePlan = false;
                    return (
                        <SelectableOptionCard
                            key={plan.planType}
                            selected={isSelected}
                            disabled={disablePlan}
                            onSelect={() => setSelectedPlanType(plan.planType)}
                            badge={isCurrent ? t('CURRENT_PLAN_LABEL') : null}
                        >
                            <div className="text-base font-semibold mb-1.5">
                                {t(plan.displayNameKey)}
                            </div>
                            <div className="text-xl font-bold mb-3">
                                {formatCurrencyAmount(priceAmount, price.currency)}
                                <span className="text-xs font-normal opacity-60">
                                    {t(selectedBillingCycle === 'yearly' ? 'PER_YEAR' : 'PER_MONTH')}
                                </span>
                            </div>
                            <ul className="list-none p-0 m-0 text-xs leading-relaxed">
                                <li>
                                    {t('EDITORS_LABEL')}: {formatLimit(plan.limits.maxEditors, t)}
                                </li>
                                <li>
                                    {t('REVIEWERS_LABEL')}: {formatLimit(plan.limits.maxReviewersPerMonth, t)}
                                </li>
                                <li>
                                    {t('HOURS_LABEL')}: {formatLimit(plan.limits.maxHoursPerMonth, t)}
                                </li>
                                <li>
                                    {t('STORAGE_LABEL')}: {isLimitUnlimited(plan.limits.maxStorageGB) ? t('UNLIMITED') : plan.limits.maxStorageGB + ' GB'}
                                </li>
                            </ul>
                        </SelectableOptionCard>
                    );
                })}
            </div>

            {isFreeFromPaid && (
                <div
                    className="p-3 mb-4 text-[13px]"
                    style={{
                        border: '1px solid rgba(255,200,0,0.4)',
                        background: 'rgba(255,200,0,0.08)',
                    }}
                >
                    {t('FREE_CANCEL_SUBSCRIPTION_HINT')}
                </div>
            )}

            {!isFreeFromPaid && isDowngrade && exceedances.length > 0 && (
                <div
                    className="p-3 mb-4 text-[13px]"
                    style={{
                        border: '1px solid rgba(255,90,90,0.5)',
                        background: 'rgba(255,90,90,0.08)',
                    }}
                >
                    <div className="font-semibold mb-1.5">{t('DOWNGRADE_WARNING_TITLE')}</div>
                    <ul className="m-0 pl-[18px]">
                        {exceedances.map((msg, idx) => (
                            <li key={idx}>{msg.message}</li>
                        ))}
                    </ul>
                </div>
            )}

            {!isSelectionUnchanged && !isFreeFromPaid && (
                <p className="text-xs opacity-70 mb-4">
                    {isDowngrade ? t('DOWNGRADE_CONFIRM_BODY') : t('UPGRADE_CONFIRM_BODY')}
                </p>
            )}

            <div className="flex gap-3 justify-end">
                <TrebbleButton onClick={onCancel} useEditorTheme={useEditorTheme} disabled={submitting}>
                    {t('CANCEL_BUTTON')}
                </TrebbleButton>
                {isFreeFromPaid ? (
                    <TrebbleButton
                        type="primary"
                        danger
                        onClick={handleCancelSubscription}
                        useEditorTheme={useEditorTheme}
                        disabled={submitting || typeof onCancelSubscription !== 'function'}
                        loading={submitting}
                    >
                        {t('CANCEL_SUBSCRIPTION_BUTTON')}
                    </TrebbleButton>
                ) : (
                    <TrebbleButton
                        type="primary"
                        onClick={handleConfirm}
                        useEditorTheme={useEditorTheme}
                        disabled={isSelectionUnchanged || submitting || hasBlockingDowngradeExceedance}
                        loading={submitting}
                    >
                        {t('CHANGE_PLAN_BUTTON')}
                    </TrebbleButton>
                )}
            </div>
        </TrebbleCard>
    );
};

export default ChangePlanContent;
export { ChangePlanContent, PLAN_CATALOG };
